import { useCallback, useEffect, useRef, useState } from "react";
import { ButtonBase, Icon, Box, styled } from "@mui/material";
import { useLocation } from "react-router-dom";
import clsx from "clsx";
import { NAV_ICONS } from "./navIcons";

const NavExpandRoot = styled("div")(({ theme }) => ({
  "& .expandIcon": {
    transition: "transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms",
    transform: "rotate(90deg)",
  },
  "& .collapseIcon": {
    transition: "transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms",
    transform: "rotate(0deg)",
  },
  "& .expansion-panel": {
    overflow: "hidden",
    transition: "max-height 0.3s cubic-bezier(0, 0, 0.2, 1)",
    paddingLeft: "27px",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    "& a": {
      fontSize: "13.5px",
      color: "#6a637d",
      fontWeight: 500,
      padding: "8px 11px",
      borderRadius: "9px",
      height: "auto",
      marginBottom: 0,
    },
    "& a:hover": {
      background: "#f8f5fd",
      color: "#17131f",
    },
    "& .navItemActive": {
      background: "linear-gradient(135deg, rgba(224,89,240,0.12), rgba(152,149,244,0.12))",
      color: "#a411c1 !important",
      fontWeight: 600,
    },
  },
  "& .highlight": {
    background: "linear-gradient(135deg, rgba(224,89,240,0.12), rgba(152,149,244,0.12))",
  },
  "&.compactNavItem": {
    width: 44,
    overflow: "hidden",
    justifyContent: "center !important",
    "& .itemText": { display: "none" },
    "& .itemIcon": { display: "none" },
  },
  "& .open": {
    background: "#f8f5fd",
    fontWeight: 600,
    color: "#17131f",
  },
}));

const BaseButton = styled(ButtonBase)(({ theme }) => ({
  height: 42,
  width: "100%",
  whiteSpace: "pre",
  overflow: "hidden",
  paddingRight: "16px",
  paddingLeft: "11px",
  borderRadius: "10px",
  marginBottom: "3px",
  display: "flex",
  justifyContent: "space-between !important",
  color: "#544d68",
  fontWeight: 500,
  "&:hover": { background: "#f8f5fd" },
  "& .icon": {
    fontSize: "18px",
    verticalAlign: "middle",
    color: "inherit",
  },
}));

const BulletIcon = styled("div")(({ theme }) => ({
  width: 4,
  height: 4,
  color: "inherit",
  overflow: "hidden",
  marginLeft: "20px",
  marginRight: "8px",
  borderRadius: "300px !important",
  background: "#544d68",
}));

const ItemText = styled("span")(({ mode }) => ({
  fontSize: "14px",
  paddingLeft: "0",
  verticalAlign: "middle",
  fontFamily: "'DM Sans', sans-serif",
  fontWeight: "inherit",
  display: mode === "compact" && "none",
}));

const BadgeValue = styled("div")(() => ({
  padding: "1px 8px",
  overflow: "hidden",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  background: "#e059f0",
  color: "#ffffff",
  minWidth: "20px",
  textAlign: "center",
}));

const MatxVerticalNavExpansionPanel = ({ item, children, mode }) => {
  const [collapsed, setCollapsed] = useState(true);
  const elementRef = useRef(null);
  const componentHeight = useRef(0);
  const { pathname } = useLocation();
  const { name, icon, iconText, badge } = item;

  const handleClick = () => {
    componentHeight.current = 0;
    calcaulateHeight(elementRef.current);
    setCollapsed(!collapsed);
  };

  const calcaulateHeight = useCallback((node) => {
    if (node.name !== "child") {
      for (let child of node.children) {
        calcaulateHeight(child);
      }
    }

    if (node.name === "child") componentHeight.current += node.scrollHeight;
    else componentHeight.current += 44; //here 44 is node height
    return;
  }, []);

  useEffect(() => {
    if (!elementRef) return;

    calcaulateHeight(elementRef.current);

    // OPEN DROPDOWN IF CHILD IS ACTIVE
    for (let child of elementRef.current.children) {
      if (child.getAttribute("href") === pathname) {
        setCollapsed(false);
      }
    }
  }, [pathname, calcaulateHeight]);

  return (
    <NavExpandRoot>
      <BaseButton
        className={clsx({
          "has-submenu compactNavItem": true,
          compactNavItem: mode === "compact",
          open: !collapsed,
        })}
        onClick={handleClick}
      >
        <Box display="flex" alignItems="center" gap="11px">
          {icon && (
            NAV_ICONS[icon] ? (
              <Box sx={{ width: 17, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>{NAV_ICONS[icon]}</Box>
            ) : (
              <Icon className="icon">{icon}</Icon>
            )
          )}
          {iconText && <BulletIcon />}
          <ItemText mode={mode} className="sidenavHoverShow">{name}</ItemText>
        </Box>

        {badge && (
          <BadgeValue className="sidenavHoverShow itemIcon">
            {badge.value}
          </BadgeValue>
        )}

        <div
          className={clsx({
            sidenavHoverShow: true,
          })}
          style={{
            transition: "transform 0.3s cubic-bezier(0, 0, 0.2, 1) 0ms",
            transform: collapsed ? "rotate(0deg)" : "rotate(90deg)",
          }}
        >
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: "#c1b8d1" }}>
            chevron_right
          </Icon>
        </div>
      </BaseButton>

      <div
        ref={elementRef}
        className="expansion-panel submenu"
        style={
          collapsed
            ? { maxHeight: "0px" }
            : { maxHeight: componentHeight.current + "px" }
        }
      >
        {children}
      </div>
    </NavExpandRoot>
  );
};

export default MatxVerticalNavExpansionPanel;
